/**
 * PO Pending Qty Cancel Screen (4.3.7)
 * Procurement Module - Orders
 * UI Layout: EXACT MATCH to PDF specification
 * 
 * Layout: Cancel pending quantities from partial POs
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Save, RefreshCw, AlertCircle, Search, X, AlertTriangle, Package } from 'lucide-react';

const POPendingQtyCancelScreen = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  
  // Form state
  const [formData, setFormData] = useState({
    poNo: '',
    poDate: '',
    supplierName: '',
    cancelReason: '',
    cancelDate: new Date().toISOString().split('T')[0],
  });

  const [poList, setPoList] = useState([]);
  const [poItems, setPoItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLookup, setShowLookup] = useState(false);

  // Theme colors
  const bgColor = isDark ? 'bg-[#1a1a2e]' : 'bg-gray-100';
  const cardBg = isDark ? 'bg-[#16213e]' : 'bg-white';
  const inputBg = isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50';
  const borderColor = isDark ? 'border-[#1f4068]' : 'border-gray-300';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const labelColor = isDark ? 'text-gray-300' : 'text-gray-700';
  const sectionBg = isDark ? 'bg-[#1f2937]' : 'bg-gray-50';

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Only show POs with pending quantities
      setPoList([
        { poNo: 'PO101L0002', poDate: '2024-01-16', supplier: 'BALAJI ENTERPRISES', items: 3, pendingQty: '30.000', status: 'Partial' },
        { poNo: 'PO101L0003', poDate: '2024-01-18', supplier: 'KRISHNA TEXTILES', items: 8, pendingQty: '200.000', status: 'Open' },
      ]);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelectPO = (po) => {
    setFormData({
      poNo: po.poNo,
      poDate: po.poDate,
      supplierName: po.supplier,
      cancelReason: '',
      cancelDate: new Date().toISOString().split('T')[0],
    });
    
    // Load PO items with pending quantities
    setPoItems([
      { id: 1, itemCode: 'IT10001', itemName: 'MENS FORMAL SHIRT', poQty: '100.000', receivedQty: '70.000', pendingQty: '30.000', cancelQty: '0.000' },
      { id: 2, itemCode: 'IT10002', itemName: 'LADIES TOP', poQty: '50.000', receivedQty: '30.000', pendingQty: '20.000', cancelQty: '0.000' },
      { id: 3, itemCode: 'IT10003', itemName: 'KIDS SHIRT', poQty: '80.000', receivedQty: '50.000', pendingQty: '30.000', cancelQty: '0.000' },
    ]);
    setSelectedItems([]);
    setShowLookup(false);
  };

  const handleToggleItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === poItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(poItems.map(item => item.id));
    }
  };

  const handleCancelQtyChange = (itemId, qty) => {
    setPoItems(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, cancelQty: qty }
        : item
    ));
  };

  const handleCancelPendingQty = () => {
    if (!formData.poNo) {
      setError('Please select a PO');
      return;
    }
    if (selectedItems.length === 0) {
      setError('Please select items to cancel');
      return;
    }
    if (!formData.cancelReason) {
      setError('Please provide a cancel reason');
      return;
    }
    
    const itemsToCancel = poItems.filter(item => selectedItems.includes(item.id));
    if (window.confirm(`Are you sure you want to cancel pending quantities for ${itemsToCancel.length} items?`)) {
      console.log('Cancelling pending qty:', { formData, items: itemsToCancel });
      alert('Pending quantities cancelled successfully!');
    }
  };

  // Input field component
  const InputField = ({ label, name, value, onChange, type = 'text', disabled, hasSearch }) => (
    <div className="flex items-center mb-2">
      <label className={`w-24 text-xs ${labelColor}`}>{label}</label>
      <div className="flex-1 flex items-center gap-1">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(name, e.target.value)}
          disabled={disabled}
          className={`w-full px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor} ${disabled ? 'opacity-60' : ''}`}
          data-testid={`input-${name}`}
        />
        {hasSearch && (
          <button className={`p-1 rounded border ${borderColor} ${inputBg} hover:bg-blue-500/20`} onClick={() => setShowLookup(true)}>
            <Search size={12} className="text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );

  // Calculate totals
  const totalPendingQty = poItems.reduce((sum, item) => sum + parseFloat(item.pendingQty || 0), 0);
  const totalCancelQty = poItems.filter(item => selectedItems.includes(item.id)).reduce((sum, item) => sum + parseFloat(item.cancelQty || item.pendingQty || 0), 0);

  return (
    <div className={`min-h-screen ${bgColor} ${textColor} p-4`} data-testid="po-pending-qty-cancel-screen">
      {/* Header */}
      <div className={`${cardBg} rounded-lg p-3 mb-4 border ${borderColor}`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">PO Pending Qty Cancel</h1>
            <p className="text-xs text-gray-400">Screen ID: 4.3.7 | Procurement Module</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} className="p-2 rounded bg-blue-600 hover:bg-blue-700 text-white" data-testid="refresh-btn">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button 
              onClick={handleCancelPendingQty} 
              className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-sm" 
              data-testid="cancel-btn" 
              disabled={selectedItems.length === 0}
            >
              <AlertTriangle size={16} /> Cancel Pending Qty
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2">
          <AlertCircle className="text-red-400" size={18} />
          <span className="text-red-400 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">×</button>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-4">
        {/* Left - PO Selection */}
        <div className={`${cardBg} rounded-lg border ${borderColor} p-4`}>
          <h3 className={`text-sm font-semibold mb-3 pb-2 border-b ${borderColor}`}>PO Selection</h3>
          <div className={`${sectionBg} rounded-lg p-3 border ${borderColor}`}>
            <InputField label="PO No" name="poNo" value={formData.poNo} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} hasSearch />
            <InputField label="PO Date" name="poDate" value={formData.poDate} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} disabled />
            <InputField label="Supplier" name="supplierName" value={formData.supplierName} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} disabled />
            <InputField label="Cancel Date" name="cancelDate" value={formData.cancelDate} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} type="date" />
            
            <div className="flex items-start mb-2 mt-3">
              <label className={`w-24 text-xs ${labelColor}`}>Reason <span className="text-red-500">*</span></label>
              <textarea
                value={formData.cancelReason}
                onChange={(e) => setFormData(p => ({ ...p, cancelReason: e.target.value }))}
                rows={3}
                className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`}
                placeholder="Enter reason..."
              />
            </div>
          </div>

          {/* Summary */}
          <div className={`mt-4 p-3 ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'} rounded-lg`}>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className={labelColor}>Total Pending:</span>
                <span className="float-right font-semibold">{totalPendingQty.toFixed(3)}</span>
              </div>
              <div>
                <span className={labelColor}>To Cancel:</span>
                <span className="float-right font-semibold text-red-400">{totalCancelQty.toFixed(3)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right - Items Grid */}
        <div className={`col-span-2 ${cardBg} rounded-lg border ${borderColor} p-4`}>
          <div className="flex items-center justify-between mb-3 pb-2 border-b ${borderColor}">
            <h3 className="text-sm font-semibold">Pending Items</h3>
            {poItems.length > 0 && (
              <button 
                onClick={handleSelectAll}
                className="px-2 py-1 text-xs rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
              >
                {selectedItems.length === poItems.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
          
          {poItems.length > 0 ? (
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-xs">
                <thead className={isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}>
                  <tr>
                    <th className="px-2 py-2 text-center w-8">
                      <input 
                        type="checkbox" 
                        checked={selectedItems.length === poItems.length}
                        onChange={handleSelectAll}
                        className="w-3 h-3"
                      />
                    </th>
                    <th className="px-2 py-2 text-left">Item Code</th>
                    <th className="px-2 py-2 text-left">Item Name</th>
                    <th className="px-2 py-2 text-right">PO Qty</th>
                    <th className="px-2 py-2 text-right">Received</th>
                    <th className="px-2 py-2 text-right">Pending</th>
                    <th className="px-2 py-2 text-right">Cancel Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {poItems.map(item => (
                    <tr key={item.id} className={`border-t ${borderColor} ${selectedItems.includes(item.id) ? (isDark ? 'bg-red-900/20' : 'bg-red-50') : ''}`}>
                      <td className="px-2 py-2 text-center">
                        <input 
                          type="checkbox" 
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleToggleItem(item.id)}
                          className="w-3 h-3"
                        />
                      </td>
                      <td className="px-2 py-2 font-mono">{item.itemCode}</td>
                      <td className="px-2 py-2">{item.itemName}</td>
                      <td className="px-2 py-2 text-right">{item.poQty}</td>
                      <td className="px-2 py-2 text-right text-emerald-400">{item.receivedQty}</td>
                      <td className="px-2 py-2 text-right text-orange-400">{item.pendingQty}</td>
                      <td className="px-2 py-2 text-right">
                        {selectedItems.includes(item.id) && (
                          <input
                            type="number"
                            value={item.cancelQty || item.pendingQty}
                            onChange={(e) => handleCancelQtyChange(item.id, e.target.value)}
                            max={parseFloat(item.pendingQty)}
                            className={`w-20 px-1 py-0.5 text-xs rounded border ${borderColor} ${inputBg} ${textColor} text-right`}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={`text-center py-8 ${labelColor}`}>
              <Package size={48} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Select a PO to view pending items</p>
            </div>
          )}
        </div>
      </div>

      {/* PO Lookup Modal */}
      {showLookup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`${cardBg} rounded-lg p-4 w-[500px] border ${borderColor}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Select PO with Pending Qty</h3>
              <button onClick={() => setShowLookup(false)}><X size={18} /></button>
            </div>
            <div className="max-h-60 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className={isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}>
                  <tr>
                    <th className="px-2 py-1 text-left">PO No</th>
                    <th className="px-2 py-1 text-left">PO Date</th>
                    <th className="px-2 py-1 text-left">Supplier</th>
                    <th className="px-2 py-1 text-right">Pending Qty</th>
                    <th className="px-2 py-1 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {poList.map(po => (
                    <tr 
                      key={po.poNo} 
                      className={`border-t ${borderColor} cursor-pointer hover:${isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50'}`}
                      onClick={() => handleSelectPO(po)}
                    >
                      <td className="px-2 py-2 font-mono text-blue-400">{po.poNo}</td>
                      <td className="px-2 py-2">{po.poDate}</td>
                      <td className="px-2 py-2">{po.supplier}</td>
                      <td className="px-2 py-2 text-right text-orange-400">{po.pendingQty}</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          po.status === 'Partial' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {po.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POPendingQtyCancelScreen;
